import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api';

// Define types
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'presenter' | 'voter';
  department: string;
  createdAt: string;
  updatedAt: string;
}

interface UserState {
  users: User[];
  selectedUser: User | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: UserState = {
  users: [],
  selectedUser: null,
  loading: false,
  error: null,
};

// Create slice
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    },
    addUser: (state, action: PayloadAction<User>) => {
      state.users.push(action.payload);
    },
    updateUser: (state, action: PayloadAction<User>) => {
      const index = state.users.findIndex(user => user.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
      if (state.selectedUser && state.selectedUser.id === action.payload.id) {
        state.selectedUser = action.payload;
      }
    },
    removeUser: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter(user => user.id !== action.payload);
      if (state.selectedUser && state.selectedUser.id === action.payload) {
        state.selectedUser = null;
      }
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle API responses
    builder.addMatcher(
      api.endpoints.getUsers.matchPending,
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      api.endpoints.getUsers.matchFulfilled,
      (state, { payload }) => {
        state.users = payload;
        state.loading = false;
      }
    );
    builder.addMatcher(
      api.endpoints.getUsers.matchRejected,
      (state, { payload }) => {
        state.loading = false;
        state.error = (payload as any)?.data?.message || 'Failed to fetch users';
      }
    );
    
    // Handle single user fetch
    builder.addMatcher(
      api.endpoints.getUserById.matchFulfilled,
      (state, { payload }) => {
        // Update in the users array if it exists
        const index = state.users.findIndex(user => user.id === payload.id);
        if (index !== -1) {
          state.users[index] = payload;
        } else {
          state.users.push(payload);
        }
        
        // Update selected user if it matches
        if (state.selectedUser && state.selectedUser.id === payload.id) {
          state.selectedUser = payload;
        }
      }
    );
  },
});

export const {
  setUsers,
  addUser,
  updateUser,
  removeUser,
  setSelectedUser,
  setLoading,
  setError,
} = userSlice.actions;

export default userSlice.reducer;
